import { StaffAbsenceKind, StaffAbsenceSource, StaffAbsenceState } from '@/enums/staffAbsence'
import i18n from '@/libs/i18n'
import { staffTimeMonthKey } from '@/libs/staffTime'
import { useFetchStore } from '@/stores/fetch'
import type { TagItem } from '@/types/Common'
import type {
    IStaffAbsence,
    IStaffAbsenceEntryBody,
    IStaffAbsenceRequest,
    IStaffAbsenceRequestBody,
    IStaffAbsenceSupersedeBody,
    IStaffHolidayCalendar,
    IStaffHolidayCalendarRequest,
    IStaffHolidayCountry,
} from '@/types/StaffAbsence'
import type { IStaffTimeMonthRecord } from '@/types/StaffTime'

/**
 * Absences, the days a house is shut, and the monthly record, as one client
 * (docs/OSEBJE.md §10).
 *
 * Everything the screens call lives here rather than in the screens, for the reason
 * `libs/staffTime.ts` gives: the personal screens read addresses OUTSIDE `/properties`
 * and the manager's screens read addresses inside it, and the difference IS the
 * permission model. One file, so it stays visible which is which.
 *
 * The wire shapes are `types/StaffAbsence.ts` and `types/StaffTime.ts`, taken field by
 * field from the API rather than guessed.
 */

/** The word for one kind of absence. The list is provisional and the screens say so. */
export const absenceKindLabel = (kind: string): string => {
    const map: Record<string, string> = {
        [StaffAbsenceKind.Vacation]:     i18n.global.t('staff_absence.kind_vacation'),
        [StaffAbsenceKind.SickLeave]:    i18n.global.t('staff_absence.kind_sick_leave'),
        [StaffAbsenceKind.Holiday]:      i18n.global.t('staff_absence.kind_holiday'),
        [StaffAbsenceKind.Training]:     i18n.global.t('staff_absence.kind_training'),
        [StaffAbsenceKind.BusinessTrip]: i18n.global.t('staff_absence.kind_business_trip'),
        [StaffAbsenceKind.Unexcused]:    i18n.global.t('staff_absence.kind_unexcused'),
        [StaffAbsenceKind.Other]:        i18n.global.t('staff_absence.kind_other'),
    }

    return map[kind] ?? kind
}

/** The icon for one kind. A grid of thirty-one cells reads faster with a glyph in it. */
export const absenceKindIcon = (kind: string): string => {
    const map: Record<string, string> = {
        [StaffAbsenceKind.Vacation]:     'i-lucide-palmtree',
        [StaffAbsenceKind.SickLeave]:    'i-lucide-thermometer',
        [StaffAbsenceKind.Holiday]:      'i-lucide-flag',
        [StaffAbsenceKind.Training]:     'i-lucide-graduation-cap',
        [StaffAbsenceKind.BusinessTrip]: 'i-lucide-briefcase',
        [StaffAbsenceKind.Unexcused]:    'i-lucide-circle-alert',
        [StaffAbsenceKind.Other]:        'i-lucide-circle-dashed',
    }

    return map[kind] ?? 'i-lucide-calendar-off'
}

/**
 * A request's state as a badge.
 *
 * A refusal is red because it is a refusal, and a withdrawal is neutral because it is
 * neither: the person changed their mind, and colouring it as a failure would be the
 * screen having an opinion about that.
 */
export const absenceStateTag = (state: string): TagItem => {
    const map: Record<string, TagItem> = {
        [StaffAbsenceState.Requested]: { label: i18n.global.t('staff_absence.state_requested'), color: 'warning' },
        [StaffAbsenceState.Approved]:  { label: i18n.global.t('staff_absence.state_approved'),  color: 'success' },
        [StaffAbsenceState.Rejected]:  { label: i18n.global.t('staff_absence.state_rejected'),  color: 'error' },
        [StaffAbsenceState.Withdrawn]: { label: i18n.global.t('staff_absence.state_withdrawn'), color: 'neutral' },
    }

    return map[state] ?? { label: state, color: 'neutral' }
}

/** Where one recorded day came from, in words. */
export const absenceSourceLabel = (source: string): string => {
    const map: Record<string, string> = {
        [StaffAbsenceSource.Request]:  i18n.global.t('staff_absence.source_request'),
        [StaffAbsenceSource.Manager]:  i18n.global.t('staff_absence.source_manager'),
        [StaffAbsenceSource.Calendar]: i18n.global.t('staff_absence.source_calendar'),
    }

    return map[source] ?? source
}

// MARK: - One's own. No property and no permission: the API narrows to the signed-in
// account, and that narrowing is the guard rather than anything on a screen.

/** One's own requests — refusals and withdrawals included, because §10 keeps them visible. */
export const loadMyAbsenceRequests = async (): Promise<IStaffAbsenceRequest[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch('/staff/me/absence-requests')
        .get()
        .json<IStaffAbsenceRequest[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

/** One's own recorded days over a range. Both ends inclusive, as everywhere in this record. */
export const loadMyAbsences = async (from: string, to: string): Promise<IStaffAbsence[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/staff/me/absences?from=${from}&to=${to}`)
        .get()
        .json<IStaffAbsence[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

/** Asking for leave. Only the requestable kinds; somebody else decides it. */
export const requestMyAbsence = async (
    request: IStaffAbsenceRequestBody,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch('/staff/me/absence-requests')
        .post(request)
        .json<{ id: string }>()

    return { ok: response.value?.status === 201, body: data.value }
}

/**
 * Taking one's own request back, and only while nobody has answered it.
 *
 * The API refuses a decided one and this is NOT where that rule lives — the screen only
 * hides the button, and the refusal is what actually holds.
 */
export const withdrawMyAbsenceRequest = async (id: string): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/staff/me/absence-requests/${id}/withdraw`)
        .post({})
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

// MARK: - The house's. Inside /properties, behind staff:approve and staff:time.

/** The manager's queue. An empty state is every request there has ever been. */
export const loadAbsenceRequests = async (
    propertyId: string,
    state: string,
): Promise<IStaffAbsenceRequest[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/absence-requests${state ? `?state=${state}` : ''}`)
        .get()
        .json<IStaffAbsenceRequest[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

/**
 * Deciding one. The API refuses a decision on one's own request (code 43) and this is not
 * where that rule lives either — the screen reflects it and says why the buttons are not
 * there, because a gap with no explanation is read as a fault and reported as one.
 */
export const decideAbsenceRequest = async (
    propertyId: string,
    id: string,
    approve: boolean,
    note: string,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/absence-requests/${id}/${approve ? 'approve' : 'reject'}`)
        .post({ note })
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/** The house's recorded days over a range. Superseded ones are left out by the server. */
export const loadAbsences = async (
    propertyId: string,
    query: string,
): Promise<IStaffAbsence[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/absences?${query}`)
        .get()
        .json<IStaffAbsence[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

/** A manager writing days into the record. It answers how many days it wrote. */
export const enterAbsences = async (
    propertyId: string,
    entry: IStaffAbsenceEntryBody,
): Promise<{ ok: boolean, days: number, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/absences`)
        .post(entry)
        .json<{ days: number }>()

    return { ok: response.value?.status === 201, days: data.value?.days ?? 0, body: data.value }
}

/**
 * Putting one day right — a NEW record pointing at the one it replaces (§10).
 *
 * It cannot move a day: only the kind and the note travel. Moving the wrong days is
 * entering the right ones and superseding the wrong ones, which is what the trail is
 * meant to read like.
 */
export const supersedeAbsence = async (
    propertyId: string,
    id: string,
    data: IStaffAbsenceSupersedeBody,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data: body } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/absences/${id}/supersede`)
        .post(data)
        .json<{ id: string }>()

    return { ok: response.value?.status === 201, body: body.value }
}

// MARK: - The monthly record and its file.

/** One month of the house's record. It is not a payroll run and the screen says so. */
export const loadStaffTimeMonth = async (
    propertyId: string,
    year: number,
    month: number,
    departmentId: string,
): Promise<IStaffTimeMonthRecord | null> => {
    const scope = departmentId ? `&department_id=${departmentId}` : ''

    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/time/month?year=${year}&month=${month}${scope}`)
        .get()
        .json<IStaffTimeMonthRecord>()

    return statusCode.value === 200 ? (data.value ?? null) : null
}

/**
 * The export's address.
 *
 * A LINK and not a fetch, deliberately: the answer is a file with a Content-Disposition
 * on it, and the browser's own download is what turns that into a file on somebody's
 * desk. Reading it through the fetch store would mean holding a month in memory and
 * building a blob to hand back to the same browser.
 *
 * It works because the session travels as a cookie as well as a header, which is exactly
 * why the cookie fallback exists (HTTP_API/Authentication.go): a link cannot set a header.
 *
 * CSV only — this service has no spreadsheet library and the approved-dependency list has
 * none either, so an XLSX button would be a button that answers a CSV with the wrong
 * extension (Logic/StaffTimeMonth.go).
 */
export const staffTimeMonthExportURL = (
    propertyId: string,
    year: number,
    month: number,
    departmentId: string,
): string => {
    const scope = departmentId ? `&department_id=${departmentId}` : ''

    return `${API_BASE}/properties/${propertyId}/staff/time/month/export` +
        `?year=${year}&month=${month}&format=csv${scope}`
}

/**
 * The payroll hand-off of one month — the file accounting imports
 * (docs/EVIDENCA-CASA.md § „Payroll Connect“).
 *
 * Beside the record's own export above rather than in `libs/staffTime.ts` where the rest
 * of the month lives, for the one reason that matters here: `API_BASE` is written out in
 * this file and nowhere else, and a second copy of it is a portal that would go on
 * fetching from one address and linking to another.
 *
 * # Two things this builder decides, and both are refusals it is avoiding
 *
 * `draft` is passed by the CALLER and never guessed here, because the server refuses an
 * open month without it (error 75) and a link that quietly added it would hand somebody a
 * draft when they believed they had the signed month. The screen knows which it is
 * looking at — it has the status strip above the table — so it says so.
 *
 * `format` is always `csv`. A house may prefer `xlsx` on its property card and this
 * service cannot write one; asking for it here would produce a refusal at the moment
 * somebody presses a button, rather than a sentence on the settings screen where the
 * preference was set.
 */
export const staffPayrollExportURL = (
    propertyId: string,
    year: number,
    month: number,
    options: { departmentId?: string, detail?: boolean, draft?: boolean } = {},
): string => {
    const scope  = options.departmentId ? `&department_id=${options.departmentId}` : ''
    const detail = options.detail ? '&detail=1' : ''
    const draft  = options.draft ? '&draft=1' : ''

    return `${API_BASE}/properties/${propertyId}/staff/time/months/${staffTimeMonthKey(year, month)}` +
        `/payroll?format=csv${scope}${detail}${draft}`
}

/**
 * One person's month as the sheet a house prints and has signed.
 *
 * No `draft` parameter, and the route has none: a person is entitled to see their own
 * month before it is signed, so an open month renders with OSNUTEK across every page
 * rather than being refused. The refusal belongs to the payroll FILE, which is loaded into
 * somebody else's system and paid out of.
 */
export const staffTimeSheetURL = (
    propertyId: string,
    year: number,
    month: number,
    userId: string,
): string =>
    `${API_BASE}/properties/${propertyId}/staff/time/months/${staffTimeMonthKey(year, month)}` +
    `/people/${userId}.pdf`

/**
 * The same sheet, one's own — no permission, and no user in the address.
 *
 * The account comes from the session (docs/OSEBJE.md §13), so there is no id here to get
 * wrong. The HOUSE is a parameter because a monthly sheet belongs to one house's record
 * and one house's close: somebody who works two hotels has one record and two houses that
 * close their months separately (docs/EVIDENCA-CASA.md §38).
 */
export const myStaffTimeSheetURL = (propertyId: string, year: number, month: number): string =>
    `${API_BASE}/staff/me/time/months/${staffTimeMonthKey(year, month)}.pdf?property_id=${propertyId}`

/**
 * Where the API answers from, written out because a LINK cannot go through the fetch
 * store and the store is the only other place that knows.
 *
 * The same literal `stores/fetch.ts` passes to `createFetch`, and it is a literal in both
 * places rather than one exported from the other: the store's is the base of a relative
 * call and this is the href of an anchor, and binding the second to the first would make
 * a change to how the portal FETCHES silently change what it LINKS to.
 */
const API_BASE = '/api/pms/v1'

// MARK: - The days a house is shut. staff:manage on all of them — a calendar is
// organisation, which §13 puts with the personnel card.

export const loadHolidayCalendars = async (propertyId: string): Promise<IStaffHolidayCalendar[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/holiday-calendars`)
        .get()
        .json<IStaffHolidayCalendar[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

export const createHolidayCalendar = async (
    propertyId: string,
    request: IStaffHolidayCalendarRequest,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/holiday-calendars`)
        .post(request)
        .json<{ id: string }>()

    return { ok: response.value?.status === 201, body: data.value }
}

export const saveHolidayCalendar = async (
    propertyId: string,
    id: string,
    request: IStaffHolidayCalendarRequest,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/holiday-calendars/${id}`)
        .put(request)
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

export const deleteHolidayCalendar = async (
    propertyId: string,
    id: string,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/holiday-calendars/${id}`)
        .delete()
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/**
 * The flag of an ISO 3166-1 alpha-2 code, as the two regional-indicator symbols it maps to.
 *
 * Derived rather than held as a table of emoji per country, and derived HERE rather than
 * sent by the API: a flag is a rendering of the code and nothing the service knows, so a
 * table of them would be a second list to keep in step with the register — and the day a
 * country is added to Logic/StaffHolidayCountries.go it would be the half somebody forgot.
 *
 * A code that is not two ASCII letters gets nothing back, so a malformed one draws no flag
 * rather than a pair of stray glyphs.
 */
export const holidayCountryFlag = (code: string): string => {
    if (!/^[A-Za-z]{2}$/.test(code)) return ''

    return String.fromCodePoint(...[...code.toUpperCase()].map(letter => 0x1F1E6 + letter.charCodeAt(0) - 65))
}

/**
 * The register of official calendars a house may seed from.
 *
 * Read from the API rather than held as a table here, so that the menu and the seed cannot
 * disagree — a country this portal offered and the service did not know would be a button
 * that refuses.
 */
export const loadHolidayCountries = async (propertyId: string): Promise<IStaffHolidayCountry[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/holiday-calendars/countries`)
        .get()
        .json<IStaffHolidayCountry[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

/**
 * "Dodaj uradne praznike" — one country's statutory work-free days.
 *
 * A seed and not a migration: the service must not assert a country's calendar over a
 * hotel's own. Pressing it again next year is safe — a date the house already carries is
 * left exactly as the house wrote it, and only missing days are added.
 *
 * It answers the calendar's id so the screen can select what it just filled, rather than
 * making somebody go and find it in a list.
 */
export const seedCountryHolidays = async (
    propertyId: string,
    country: string,
): Promise<{ ok: boolean, id: string, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/holiday-calendars/seed`)
        .post({ country })
        .json<{ id: string }>()

    return { ok: response.value?.status === 200, id: data.value?.id ?? '', body: data.value }
}
